import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Download } from "lucide-react";
import { describeAuthError, exportVault } from "../../app/authFlow.js";
import { ApiClientError } from "../../api/http.js";
import { useSession } from "../../app/session.js";
import { useMe } from "../../data/account.js";
import { Dialog } from "../../ui/Dialog.js";
import { Checkbox, Field, SecretInput } from "../../ui/forms.js";
import { Alert, Button, Section } from "../../ui/primitives.js";
import { useToast } from "../../ui/Toast.js";

function download(fileName: string, json: string): void {
  const url = URL.createObjectURL(new Blob([json], { type: "application/json" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.setTimeout((): void => URL.revokeObjectURL(url), 1000);
}

/** Settings: plaintext export of the personal vault, unless an admin policy blocks it. */
export function ExportSection(): ReactNode {
  const me = useMe();
  const [open, setOpen] = useState(false);
  const blocked = me.data?.policies.exportsDisabled === true;

  return (
    <Section
      title="Tresor exportieren"
      description="Lädt deine persönlichen Einträge als unverschlüsselte JSON-Datei herunter. Team-Einträge sind nicht enthalten."
    >
      {blocked ? (
        <Alert tone="info">Dein Administrator hat den Export gesperrt.</Alert>
      ) : (
        <div className="flex flex-col gap-3 rounded-lg border border-line bg-ink-2 p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm leading-relaxed text-mist">Der Export wird protokolliert und du bekommst eine E-Mail.</p>
          <Button variant="secondary" icon={Download} disabled={me.data === undefined} onClick={(): void => setOpen(true)}>
            Exportieren
          </Button>
        </div>
      )}
      {open ? <ExportDialog onClose={(): void => setOpen(false)} /> : null}
    </Section>
  );
}

function ExportDialog(props: { readonly onClose: () => void }): ReactNode {
  const { state } = useSession();
  const toast = useToast();
  const [password, setPassword] = useState("");
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy || state.status !== "unlocked") {
      return;
    }
    const problem = password === "" ? "Bitte gib dein Master-Passwort ein." : understood ? undefined : "Bitte bestätige den Hinweis.";
    setError(problem);
    if (problem !== undefined) {
      return;
    }
    setBusy(true);
    try {
      const result = await exportVault(state.client, state.email, password, new Date());
      download(result.fileName, result.json);
      const missing = result.skipped > 0 ? ` ${result.skipped} Einträge konnten nicht entschlüsselt werden und fehlen.` : "";
      toast.notify(`${result.count} Einträge exportiert.${missing}`, { tone: result.skipped > 0 ? "danger" : "success" });
      props.onClose();
    } catch (err: unknown) {
      setError(err instanceof ApiClientError && err.status === 403 ? err.message : describeAuthError(err));
    } finally {
      setPassword("");
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onClose={(): void => {
        if (!busy) {
          props.onClose();
        }
      }}
      dismissible={!busy}
      title="Tresor exportieren"
      description="Bestätige mit deinem Master-Passwort. Die Datei entsteht in diesem Browser."
      variant="center"
      size="sm"
    >
      <form onSubmit={(event): void => void submit(event)} className="flex flex-col gap-5 p-5" noValidate>
        <Alert tone="danger">Die Datei enthält alle Passwörter und die privaten Schlüssel deiner Passkeys im Klartext. Speichere sie nur kurz und lösche sie nach dem Import.</Alert>
        <Field id="export-password" label="Master-Passwort">
          <SecretInput id="export-password" autoComplete="current-password" autoFocus value={password} disabled={busy} onChange={(event): void => setPassword(event.target.value)} revealLabel="Master-Passwort" />
        </Field>
        <Checkbox id="export-understood" checked={understood} onChange={setUnderstood}>
          Ich verstehe, dass die Datei unverschlüsselt ist.
        </Checkbox>
        {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={props.onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" icon={Download} loading={busy}>
            Exportieren
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
